import type { MessageBundle } from '@amzn/arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import { calculateEffectiveAdValoremRate } from '@xbcb/entry-utils/dist/lib/calculateEffectiveAdValoremRate';
import { US_ENTRY_CURRENCY } from 'constants/currency';
import { formatCurrencyByCurrencyCode } from 'libs/display';
import { HtsObject } from 'types';

const SECTION_232_CAP_RATES: Record<string, number> = {
  '99038207': 10,
  '99038210': 15,
};

const getSection232CapRate = (htsNumber?: string): number | undefined => {
  if (!htsNumber) return undefined;
  const cleaned = htsNumber.replace(/\./g, '');
  for (const [prefix, cap] of Object.entries(SECTION_232_CAP_RATES)) {
    if (cleaned.startsWith(prefix)) return cap;
  }
  return undefined;
};

export const getRowLabel = (
  index: number,
  isPrimary: boolean,
  bundle: MessageBundle,
  htsObj?: HtsObject,
): string => {
  if (isPrimary) {
    return htsObj?.text
      ? safeGetMessage(
          bundle,
          'primary_hts_label_with_text',
          { text: htsObj.text },
          `Primary HTS - ${htsObj.text}`,
        )
      : safeGetMessage(bundle, 'primary_hts_label', undefined, 'Primary HTS');
  }
  if (htsObj?.additionalTariffType) {
    return safeGetMessage(
      bundle,
      `tariff_type_${htsObj.additionalTariffType}`,
      undefined,
      htsObj.additionalTariffType,
    );
  }
  if (htsObj?.text) {
    const text =
      htsObj.text.length > 30 ? `${htsObj.text.slice(0, 30)}...` : htsObj.text;
    return safeGetMessage(
      bundle,
      'additional_hts_label_with_text',
      { text },
      `Additional HTS - ${text}`,
    );
  }
  return safeGetMessage(
    bundle,
    'additional_hts_label',
    { index: index + 1 },
    `Additional HTS #${index + 1}`,
  );
};

export const formatDutyRate = (htsObj?: HtsObject): string | undefined => {
  if (!htsObj?.rate) return undefined;
  const { adValorem, specific } = htsObj.rate;
  if (adValorem != null) return `${parseFloat((adValorem * 100).toFixed(2))}%`;
  if (specific != null) return `${specific}`;
  return undefined;
};

export const computeDutyValue = (
  htsObj: HtsObject | undefined,
  totalValue: number,
): string | undefined => {
  if (!htsObj?.rate) return undefined;
  const { adValorem, specific } = htsObj.rate;
  if (adValorem != null) return `$${(totalValue * adValorem).toFixed(2)}`;
  if (specific != null) return `$${(totalValue * specific).toFixed(2)}`;
  return undefined;
};

export interface Section232CappedRateParams {
  /** The additional-tariff row's HTS number (e.g. "9903.82.10"). */
  htsNumber?: string;
  /** The line's primary HTS record. */
  primaryHtsObj?: HtsObject;
  /** The line's primary HTS number. */
  primaryHtsNumber?: string;
  /** The line/product total value. */
  lineValue: number;
  /** The primary tariff's reporting quantities (for specific-rate conversion). */
  lineReporting?: Array<{ value?: number | null; unit?: string | null }>;
}

/**
 * Returns the Section 232 capped rate (cap - primary effective rate, floored
 * at 0) for HTS 9903.82.07 / 9903.82.10, or undefined for other codes.
 *
 * @param {Section232CappedRateParams} params The 232 row and its primary HTS rate context.
 * @return {number | undefined} The capped ad valorem rate as a percent, or undefined when not applicable.
 */
export const getSection232CappedAdValoremRate = ({
  htsNumber,
  primaryHtsObj,
  primaryHtsNumber,
  lineValue,
  lineReporting,
}: Section232CappedRateParams): number | undefined => {
  const capRate = getSection232CapRate(htsNumber);
  if (capRate === undefined) return undefined;
  if (!primaryHtsNumber || !primaryHtsObj?.rate) return undefined;
  const { effectiveAdValoremRate } = calculateEffectiveAdValoremRate({
    baseHtsNumber: primaryHtsNumber.replace(/\./g, ''),
    lineItemValue: lineValue,
    lineItemReporting: lineReporting,
    htsRate: {
      adValorem: primaryHtsObj.rate.adValorem ?? undefined,
      specific: primaryHtsObj.rate.specific ?? undefined,
      rateCode: primaryHtsObj.rate.rateCode ?? undefined,
    },
  });
  return Math.max(0, capRate - effectiveAdValoremRate);
};

/**
 * Formats an ad valorem percent for display, e.g. 14.3 -> "14.3%".
 *
 * @param {number} percent The ad valorem rate as a percent.
 * @return {string} The formatted percent string.
 */
export const formatAdValoremPercent = (percent: number): string =>
  `${parseFloat(percent.toFixed(2))}%`;

export interface RawEvaluatorLine {
  value?: unknown;
  assist?: unknown;
  tariffs?: { unitValue?: { value?: unknown } }[];
  [key: string]: unknown;
}

// Coerce a raw form field to a number, mapping blank inputs to `NaN` so a real
// `0` is distinguishable from "not entered" (`Number('')` is `0`).
const toNumber = (v: unknown): number =>
  v === null || v === undefined || v === '' ? NaN : Number(v);

// Resolve the line's customs `value`: entered value, else (when
// `allowDerivedValueFallback`) sum(unitValue) * quantity if positive, else unset.
// `assist` is coerced only when finite; other fields pass through. The fallback
// is gated on the evaluated-duty display flag so PROD never derives a value.
export const resolveEvaluatorLine = <T extends RawEvaluatorLine>(
  rawLine: T,
  product: { quantity?: unknown } | undefined,
  allowDerivedValueFallback: boolean,
): T => {
  const enteredValue = toNumber(rawLine.value);
  const enteredAssist = toNumber(rawLine.assist);
  const quantity = Number(product?.quantity) || 1;

  const lineUnitValue = (rawLine.tariffs ?? []).reduce((sum, tariff) => {
    const unitValue = Number(tariff?.unitValue?.value);
    return sum + (Number.isFinite(unitValue) ? unitValue : 0);
  }, 0);
  const derivedValue = lineUnitValue * quantity;

  const resolvedValue = Number.isFinite(enteredValue)
    ? enteredValue
    : allowDerivedValueFallback && derivedValue > 0
    ? derivedValue
    : NaN;

  return {
    ...rawLine,
    ...(Number.isFinite(resolvedValue) && { value: resolvedValue }),
    ...(Number.isFinite(enteredAssist) && { assist: enteredAssist }),
  };
};

export const getEvaluatedDuty = (charges?: {
  dutydisplay?: string;
  duties?: number;
}): { rate: string; value: string } | undefined => {
  if (!charges?.dutydisplay || !Number.isFinite(charges.duties)) {
    return undefined;
  }
  const value = formatCurrencyByCurrencyCode(
    US_ENTRY_CURRENCY,
    charges.duties,
    2,
  );
  // Rate and value travel together: never pair an evaluated rate with a
  // locally estimated amount (or vice versa).
  if (value === undefined) return undefined;
  return { rate: charges.dutydisplay, value };
};
